/**
 * countries.ts
 * Comprehensive list of countries for the Settings flag picker.
 *
 * Each entry pairs a flag emoji with the full country name. The emoji is the
 * value stored in `prefs.flag` (server-side), so picking from this list
 * remains backwards-compatible with existing UserProfilePanel/LeaderboardPanel
 * code that does `flagImgUrl(prefs.flag)` (extracts the ISO code from emoji).
 *
 * Special sentinel: the first entry, "EyeWire", uses the value 'eyewire' rather
 * than a flag emoji. Renderers should detect this and show the EyeWire logo.
 */

export interface CountryEntry {
  /** Storage value: either a flag emoji ("🇺🇸") or the sentinel string 'eyewire'. */
  code: string;
  /** Human-readable label used in the dropdown. */
  name: string;
  /** Emoji glyph (for emoji-based renderers, e.g. text-only fallback). */
  emoji: string;
}

/** Sentinel used by `prefs.flag` to indicate the EyeWire logo. */
export const EYEWIRE_FLAG = 'eyewire';

export const COUNTRIES: CountryEntry[] = [
  { code: 'eyewire',  name: 'EyeWire',                    emoji: '◈' },
  { code: '🇦🇫', name: 'Afghanistan',                emoji: '🇦🇫' },
  { code: '🇦🇱', name: 'Albania',                    emoji: '🇦🇱' },
  { code: '🇩🇿', name: 'Algeria',                    emoji: '🇩🇿' },
  { code: '🇦🇸', name: 'American Samoa',             emoji: '🇦🇸' },
  { code: '🇦🇩', name: 'Andorra',                    emoji: '🇦🇩' },
  { code: '🇦🇴', name: 'Angola',                     emoji: '🇦🇴' },
  { code: '🇦🇮', name: 'Anguilla',                   emoji: '🇦🇮' },
  { code: '🇦🇶', name: 'Antarctica',                 emoji: '🇦🇶' },
  { code: '🇦🇬', name: 'Antigua and Barbuda',        emoji: '🇦🇬' },
  { code: '🇦🇷', name: 'Argentina',                  emoji: '🇦🇷' },
  { code: '🇦🇲', name: 'Armenia',                    emoji: '🇦🇲' },
  { code: '🇦🇼', name: 'Aruba',                      emoji: '🇦🇼' },
  { code: '🇦🇺', name: 'Australia',                  emoji: '🇦🇺' },
  { code: '🇦🇹', name: 'Austria',                    emoji: '🇦🇹' },
  { code: '🇦🇿', name: 'Azerbaijan',                 emoji: '🇦🇿' },
  { code: '🇧🇸', name: 'Bahamas',                    emoji: '🇧🇸' },
  { code: '🇧🇭', name: 'Bahrain',                    emoji: '🇧🇭' },
  { code: '🇧🇩', name: 'Bangladesh',                 emoji: '🇧🇩' },
  { code: '🇧🇧', name: 'Barbados',                   emoji: '🇧🇧' },
  { code: '🇧🇾', name: 'Belarus',                    emoji: '🇧🇾' },
  { code: '🇧🇪', name: 'Belgium',                    emoji: '🇧🇪' },
  { code: '🇧🇿', name: 'Belize',                     emoji: '🇧🇿' },
  { code: '🇧🇯', name: 'Benin',                      emoji: '🇧🇯' },
  { code: '🇧🇲', name: 'Bermuda',                    emoji: '🇧🇲' },
  { code: '🇧🇹', name: 'Bhutan',                     emoji: '🇧🇹' },
  { code: '🇧🇴', name: 'Bolivia',                    emoji: '🇧🇴' },
  { code: '🇧🇦', name: 'Bosnia and Herzegovina',     emoji: '🇧🇦' },
  { code: '🇧🇼', name: 'Botswana',                   emoji: '🇧🇼' },
  { code: '🇧🇷', name: 'Brazil',                     emoji: '🇧🇷' },
  { code: '🇮🇴', name: 'British Indian Ocean Territory', emoji: '🇮🇴' },
  { code: '🇻🇬', name: 'British Virgin Islands',     emoji: '🇻🇬' },
  { code: '🇧🇳', name: 'Brunei',                     emoji: '🇧🇳' },
  { code: '🇧🇬', name: 'Bulgaria',                   emoji: '🇧🇬' },
  { code: '🇧🇫', name: 'Burkina Faso',               emoji: '🇧🇫' },
  { code: '🇧🇮', name: 'Burundi',                    emoji: '🇧🇮' },
  { code: '🇰🇭', name: 'Cambodia',                   emoji: '🇰🇭' },
  { code: '🇨🇲', name: 'Cameroon',                   emoji: '🇨🇲' },
  { code: '🇨🇦', name: 'Canada',                     emoji: '🇨🇦' },
  { code: '🇨🇻', name: 'Cape Verde',                 emoji: '🇨🇻' },
  { code: '🇰🇾', name: 'Cayman Islands',             emoji: '🇰🇾' },
  { code: '🇨🇫', name: 'Central African Republic',   emoji: '🇨🇫' },
  { code: '🇹🇩', name: 'Chad',                       emoji: '🇹🇩' },
  { code: '🇨🇱', name: 'Chile',                      emoji: '🇨🇱' },
  { code: '🇨🇳', name: 'China',                      emoji: '🇨🇳' },
  { code: '🇨🇽', name: 'Christmas Island',           emoji: '🇨🇽' },
  { code: '🇨🇨', name: 'Cocos Islands',              emoji: '🇨🇨' },
  { code: '🇨🇴', name: 'Colombia',                   emoji: '🇨🇴' },
  { code: '🇰🇲', name: 'Comoros',                    emoji: '🇰🇲' },
  { code: '🇨🇰', name: 'Cook Islands',               emoji: '🇨🇰' },
  { code: '🇨🇷', name: 'Costa Rica',                 emoji: '🇨🇷' },
  { code: '🇭🇷', name: 'Croatia',                    emoji: '🇭🇷' },
  { code: '🇨🇺', name: 'Cuba',                       emoji: '🇨🇺' },
  { code: '🇨🇼', name: 'Curaçao',                    emoji: '🇨🇼' },
  { code: '🇨🇾', name: 'Cyprus',                     emoji: '🇨🇾' },
  { code: '🇨🇿', name: 'Czech Republic',             emoji: '🇨🇿' },
  { code: '🇨🇩', name: 'Democratic Republic of the Congo', emoji: '🇨🇩' },
  { code: '🇩🇰', name: 'Denmark',                    emoji: '🇩🇰' },
  { code: '🇩🇯', name: 'Djibouti',                   emoji: '🇩🇯' },
  { code: '🇩🇲', name: 'Dominica',                   emoji: '🇩🇲' },
  { code: '🇩🇴', name: 'Dominican Republic',         emoji: '🇩🇴' },
  { code: '🇪🇨', name: 'Ecuador',                    emoji: '🇪🇨' },
  { code: '🇪🇬', name: 'Egypt',                      emoji: '🇪🇬' },
  { code: '🇸🇻', name: 'El Salvador',                emoji: '🇸🇻' },
  { code: '🇬🇶', name: 'Equatorial Guinea',          emoji: '🇬🇶' },
  { code: '🇪🇷', name: 'Eritrea',                    emoji: '🇪🇷' },
  { code: '🇪🇪', name: 'Estonia',                    emoji: '🇪🇪' },
  { code: '🇸🇿', name: 'Eswatini',                   emoji: '🇸🇿' },
  { code: '🇪🇹', name: 'Ethiopia',                   emoji: '🇪🇹' },
  { code: '🇫🇰', name: 'Falkland Islands',           emoji: '🇫🇰' },
  { code: '🇫🇴', name: 'Faroe Islands',              emoji: '🇫🇴' },
  { code: '🇫🇯', name: 'Fiji',                       emoji: '🇫🇯' },
  { code: '🇫🇮', name: 'Finland',                    emoji: '🇫🇮' },
  { code: '🇫🇷', name: 'France',                     emoji: '🇫🇷' },
  { code: '🇬🇫', name: 'French Guiana',              emoji: '🇬🇫' },
  { code: '🇵🇫', name: 'French Polynesia',           emoji: '🇵🇫' },
  { code: '🇬🇦', name: 'Gabon',                      emoji: '🇬🇦' },
  { code: '🇬🇲', name: 'Gambia',                     emoji: '🇬🇲' },
  { code: '🇬🇪', name: 'Georgia',                    emoji: '🇬🇪' },
  { code: '🇩🇪', name: 'Germany',                    emoji: '🇩🇪' },
  { code: '🇬🇭', name: 'Ghana',                      emoji: '🇬🇭' },
  { code: '🇬🇮', name: 'Gibraltar',                  emoji: '🇬🇮' },
  { code: '🇬🇷', name: 'Greece',                     emoji: '🇬🇷' },
  { code: '🇬🇱', name: 'Greenland',                  emoji: '🇬🇱' },
  { code: '🇬🇩', name: 'Grenada',                    emoji: '🇬🇩' },
  { code: '🇬🇵', name: 'Guadeloupe',                 emoji: '🇬🇵' },
  { code: '🇬🇺', name: 'Guam',                       emoji: '🇬🇺' },
  { code: '🇬🇹', name: 'Guatemala',                  emoji: '🇬🇹' },
  { code: '🇬🇬', name: 'Guernsey',                   emoji: '🇬🇬' },
  { code: '🇬🇳', name: 'Guinea',                     emoji: '🇬🇳' },
  { code: '🇬🇼', name: 'Guinea-Bissau',              emoji: '🇬🇼' },
  { code: '🇬🇾', name: 'Guyana',                     emoji: '🇬🇾' },
  { code: '🇭🇹', name: 'Haiti',                      emoji: '🇭🇹' },
  { code: '🇭🇳', name: 'Honduras',                   emoji: '🇭🇳' },
  { code: '🇭🇰', name: 'Hong Kong',                  emoji: '🇭🇰' },
  { code: '🇭🇺', name: 'Hungary',                    emoji: '🇭🇺' },
  { code: '🇮🇸', name: 'Iceland',                    emoji: '🇮🇸' },
  { code: '🇮🇳', name: 'India',                      emoji: '🇮🇳' },
  { code: '🇮🇩', name: 'Indonesia',                  emoji: '🇮🇩' },
  { code: '🇮🇷', name: 'Iran',                       emoji: '🇮🇷' },
  { code: '🇮🇶', name: 'Iraq',                       emoji: '🇮🇶' },
  { code: '🇮🇪', name: 'Ireland',                    emoji: '🇮🇪' },
  { code: '🇮🇲', name: 'Isle of Man',                emoji: '🇮🇲' },
  { code: '🇮🇱', name: 'Israel',                     emoji: '🇮🇱' },
  { code: '🇮🇹', name: 'Italy',                      emoji: '🇮🇹' },
  { code: '🇨🇮', name: 'Ivory Coast',                emoji: '🇨🇮' },
  { code: '🇯🇲', name: 'Jamaica',                    emoji: '🇯🇲' },
  { code: '🇯🇵', name: 'Japan',                      emoji: '🇯🇵' },
  { code: '🇯🇪', name: 'Jersey',                     emoji: '🇯🇪' },
  { code: '🇯🇴', name: 'Jordan',                     emoji: '🇯🇴' },
  { code: '🇰🇿', name: 'Kazakhstan',                 emoji: '🇰🇿' },
  { code: '🇰🇪', name: 'Kenya',                      emoji: '🇰🇪' },
  { code: '🇰🇮', name: 'Kiribati',                   emoji: '🇰🇮' },
  { code: '🇽🇰', name: 'Kosovo',                     emoji: '🇽🇰' },
  { code: '🇰🇼', name: 'Kuwait',                     emoji: '🇰🇼' },
  { code: '🇰🇬', name: 'Kyrgyzstan',                 emoji: '🇰🇬' },
  { code: '🇱🇦', name: 'Laos',                       emoji: '🇱🇦' },
  { code: '🇱🇻', name: 'Latvia',                     emoji: '🇱🇻' },
  { code: '🇱🇧', name: 'Lebanon',                    emoji: '🇱🇧' },
  { code: '🇱🇸', name: 'Lesotho',                    emoji: '🇱🇸' },
  { code: '🇱🇷', name: 'Liberia',                    emoji: '🇱🇷' },
  { code: '🇱🇾', name: 'Libya',                      emoji: '🇱🇾' },
  { code: '🇱🇮', name: 'Liechtenstein',              emoji: '🇱🇮' },
  { code: '🇱🇹', name: 'Lithuania',                  emoji: '🇱🇹' },
  { code: '🇱🇺', name: 'Luxembourg',                 emoji: '🇱🇺' },
  { code: '🇲🇴', name: 'Macao',                      emoji: '🇲🇴' },
  { code: '🇲🇬', name: 'Madagascar',                 emoji: '🇲🇬' },
  { code: '🇲🇼', name: 'Malawi',                     emoji: '🇲🇼' },
  { code: '🇲🇾', name: 'Malaysia',                   emoji: '🇲🇾' },
  { code: '🇲🇻', name: 'Maldives',                   emoji: '🇲🇻' },
  { code: '🇲🇱', name: 'Mali',                       emoji: '🇲🇱' },
  { code: '🇲🇹', name: 'Malta',                      emoji: '🇲🇹' },
  { code: '🇲🇭', name: 'Marshall Islands',           emoji: '🇲🇭' },
  { code: '🇲🇶', name: 'Martinique',                 emoji: '🇲🇶' },
  { code: '🇲🇷', name: 'Mauritania',                 emoji: '🇲🇷' },
  { code: '🇲🇺', name: 'Mauritius',                  emoji: '🇲🇺' },
  { code: '🇾🇹', name: 'Mayotte',                    emoji: '🇾🇹' },
  { code: '🇲🇽', name: 'Mexico',                     emoji: '🇲🇽' },
  { code: '🇫🇲', name: 'Micronesia',                 emoji: '🇫🇲' },
  { code: '🇲🇩', name: 'Moldova',                    emoji: '🇲🇩' },
  { code: '🇲🇨', name: 'Monaco',                     emoji: '🇲🇨' },
  { code: '🇲🇳', name: 'Mongolia',                   emoji: '🇲🇳' },
  { code: '🇲🇪', name: 'Montenegro',                 emoji: '🇲🇪' },
  { code: '🇲🇸', name: 'Montserrat',                 emoji: '🇲🇸' },
  { code: '🇲🇦', name: 'Morocco',                    emoji: '🇲🇦' },
  { code: '🇲🇿', name: 'Mozambique',                 emoji: '🇲🇿' },
  { code: '🇲🇲', name: 'Myanmar',                    emoji: '🇲🇲' },
  { code: '🇳🇦', name: 'Namibia',                    emoji: '🇳🇦' },
  { code: '🇳🇷', name: 'Nauru',                      emoji: '🇳🇷' },
  { code: '🇳🇵', name: 'Nepal',                      emoji: '🇳🇵' },
  { code: '🇳🇱', name: 'Netherlands',                emoji: '🇳🇱' },
  { code: '🇳🇨', name: 'New Caledonia',              emoji: '🇳🇨' },
  { code: '🇳🇿', name: 'New Zealand',                emoji: '🇳🇿' },
  { code: '🇳🇮', name: 'Nicaragua',                  emoji: '🇳🇮' },
  { code: '🇳🇪', name: 'Niger',                      emoji: '🇳🇪' },
  { code: '🇳🇬', name: 'Nigeria',                    emoji: '🇳🇬' },
  { code: '🇳🇺', name: 'Niue',                       emoji: '🇳🇺' },
  { code: '🇰🇵', name: 'North Korea',                emoji: '🇰🇵' },
  { code: '🇲🇰', name: 'North Macedonia',            emoji: '🇲🇰' },
  { code: '🇲🇵', name: 'Northern Mariana Islands',   emoji: '🇲🇵' },
  { code: '🇳🇴', name: 'Norway',                     emoji: '🇳🇴' },
  { code: '🇴🇲', name: 'Oman',                       emoji: '🇴🇲' },
  { code: '🇵🇰', name: 'Pakistan',                   emoji: '🇵🇰' },
  { code: '🇵🇼', name: 'Palau',                      emoji: '🇵🇼' },
  { code: '🇵🇸', name: 'Palestine',                  emoji: '🇵🇸' },
  { code: '🇵🇦', name: 'Panama',                     emoji: '🇵🇦' },
  { code: '🇵🇬', name: 'Papua New Guinea',           emoji: '🇵🇬' },
  { code: '🇵🇾', name: 'Paraguay',                   emoji: '🇵🇾' },
  { code: '🇵🇪', name: 'Peru',                       emoji: '🇵🇪' },
  { code: '🇵🇭', name: 'Philippines',                emoji: '🇵🇭' },
  { code: '🇵🇳', name: 'Pitcairn',                   emoji: '🇵🇳' },
  { code: '🇵🇱', name: 'Poland',                     emoji: '🇵🇱' },
  { code: '🇵🇹', name: 'Portugal',                   emoji: '🇵🇹' },
  { code: '🇵🇷', name: 'Puerto Rico',                emoji: '🇵🇷' },
  { code: '🇶🇦', name: 'Qatar',                      emoji: '🇶🇦' },
  { code: '🇨🇬', name: 'Republic of the Congo',      emoji: '🇨🇬' },
  { code: '🇷🇪', name: 'Réunion',                    emoji: '🇷🇪' },
  { code: '🇷🇴', name: 'Romania',                    emoji: '🇷🇴' },
  { code: '🇷🇺', name: 'Russia',                     emoji: '🇷🇺' },
  { code: '🇷🇼', name: 'Rwanda',                     emoji: '🇷🇼' },
  { code: '🇼🇸', name: 'Samoa',                      emoji: '🇼🇸' },
  { code: '🇸🇲', name: 'San Marino',                 emoji: '🇸🇲' },
  { code: '🇸🇹', name: 'São Tomé and Príncipe',      emoji: '🇸🇹' },
  { code: '🇸🇦', name: 'Saudi Arabia',               emoji: '🇸🇦' },
  { code: '🇸🇳', name: 'Senegal',                    emoji: '🇸🇳' },
  { code: '🇷🇸', name: 'Serbia',                     emoji: '🇷🇸' },
  { code: '🇸🇨', name: 'Seychelles',                 emoji: '🇸🇨' },
  { code: '🇸🇱', name: 'Sierra Leone',               emoji: '🇸🇱' },
  { code: '🇸🇬', name: 'Singapore',                  emoji: '🇸🇬' },
  { code: '🇸🇽', name: 'Sint Maarten',               emoji: '🇸🇽' },
  { code: '🇸🇰', name: 'Slovakia',                   emoji: '🇸🇰' },
  { code: '🇸🇮', name: 'Slovenia',                   emoji: '🇸🇮' },
  { code: '🇸🇧', name: 'Solomon Islands',            emoji: '🇸🇧' },
  { code: '🇸🇴', name: 'Somalia',                    emoji: '🇸🇴' },
  { code: '🇿🇦', name: 'South Africa',               emoji: '🇿🇦' },
  { code: '🇰🇷', name: 'South Korea',                emoji: '🇰🇷' },
  { code: '🇸🇸', name: 'South Sudan',                emoji: '🇸🇸' },
  { code: '🇪🇸', name: 'Spain',                      emoji: '🇪🇸' },
  { code: '🇱🇰', name: 'Sri Lanka',                  emoji: '🇱🇰' },
  { code: '🇰🇳', name: 'Saint Kitts and Nevis',      emoji: '🇰🇳' },
  { code: '🇱🇨', name: 'Saint Lucia',                emoji: '🇱🇨' },
  { code: '🇲🇫', name: 'Saint Martin',               emoji: '🇲🇫' },
  { code: '🇻🇨', name: 'Saint Vincent and the Grenadines', emoji: '🇻🇨' },
  { code: '🇸🇩', name: 'Sudan',                      emoji: '🇸🇩' },
  { code: '🇸🇷', name: 'Suriname',                   emoji: '🇸🇷' },
  { code: '🇸🇪', name: 'Sweden',                     emoji: '🇸🇪' },
  { code: '🇨🇭', name: 'Switzerland',                emoji: '🇨🇭' },
  { code: '🇸🇾', name: 'Syria',                      emoji: '🇸🇾' },
  { code: '🇹🇼', name: 'Taiwan',                     emoji: '🇹🇼' },
  { code: '🇹🇯', name: 'Tajikistan',                 emoji: '🇹🇯' },
  { code: '🇹🇿', name: 'Tanzania',                   emoji: '🇹🇿' },
  { code: '🇹🇭', name: 'Thailand',                   emoji: '🇹🇭' },
  { code: '🇹🇱', name: 'Timor-Leste',                emoji: '🇹🇱' },
  { code: '🇹🇬', name: 'Togo',                       emoji: '🇹🇬' },
  { code: '🇹🇰', name: 'Tokelau',                    emoji: '🇹🇰' },
  { code: '🇹🇴', name: 'Tonga',                      emoji: '🇹🇴' },
  { code: '🇹🇹', name: 'Trinidad and Tobago',        emoji: '🇹🇹' },
  { code: '🇹🇳', name: 'Tunisia',                    emoji: '🇹🇳' },
  { code: '🇹🇷', name: 'Turkey',                     emoji: '🇹🇷' },
  { code: '🇹🇲', name: 'Turkmenistan',               emoji: '🇹🇲' },
  { code: '🇹🇨', name: 'Turks and Caicos Islands',   emoji: '🇹🇨' },
  { code: '🇹🇻', name: 'Tuvalu',                     emoji: '🇹🇻' },
  { code: '🇺🇬', name: 'Uganda',                     emoji: '🇺🇬' },
  { code: '🇺🇦', name: 'Ukraine',                    emoji: '🇺🇦' },
  { code: '🇦🇪', name: 'United Arab Emirates',       emoji: '🇦🇪' },
  { code: '🇬🇧', name: 'United Kingdom',             emoji: '🇬🇧' },
  { code: '🇺🇸', name: 'United States',              emoji: '🇺🇸' },
  { code: '🇺🇾', name: 'Uruguay',                    emoji: '🇺🇾' },
  { code: '🇺🇿', name: 'Uzbekistan',                 emoji: '🇺🇿' },
  { code: '🇻🇺', name: 'Vanuatu',                    emoji: '🇻🇺' },
  { code: '🇻🇦', name: 'Vatican City',               emoji: '🇻🇦' },
  { code: '🇻🇪', name: 'Venezuela',                  emoji: '🇻🇪' },
  { code: '🇻🇳', name: 'Vietnam',                    emoji: '🇻🇳' },
  { code: '🇼🇫', name: 'Wallis and Futuna',          emoji: '🇼🇫' },
  { code: '🇪🇭', name: 'Western Sahara',             emoji: '🇪🇭' },
  { code: '🇾🇪', name: 'Yemen',                      emoji: '🇾🇪' },
  { code: '🇿🇲', name: 'Zambia',                     emoji: '🇿🇲' },
  { code: '🇿🇼', name: 'Zimbabwe',                   emoji: '🇿🇼' },
];

/** Look up a country entry by its stored code (emoji or 'eyewire'). */
export function findCountryByCode(code: string): CountryEntry | null {
  return COUNTRIES.find(c => c.code === code) ?? null;
}
